"use client";

import Link from "next/link";
import React from "react";
import Button from "./Button";

export type PrimaryPromotionBannerProps = {
  title: string;
  highlightRed?: boolean;
  subtitle?: string;
  description?: string;
  align?: "left" | "center" | "right";
  buttonLabel?: string;
  buttonLink?: string;
  buttonOnClick?: () => void;
};

/* 🔹 Helper: render `\n` as line breaks */
const renderWithLineBreaks = (text?: string) => {
  if (!text) return null;
  return text.split("\n").map((line, index, arr) => (
    <React.Fragment key={index}>
      {line}
      {index < arr.length - 1 && <br />}
    </React.Fragment>
  ));
};

const PrimaryPromotionBanner: React.FC<PrimaryPromotionBannerProps> = ({
  title,
  highlightRed = false,
  subtitle,
  description,
  align = "center",
  buttonLabel,
  buttonLink,
  buttonOnClick,
}) => {
  const alignment = {
    left: "text-left items-start",
    center: "text-center items-center",
    right: "text-right items-end",
  };

  return (
    <section className="relative custom-container overflow-hidden">
      <div
        className={`flex flex-col bg-white/10 backdrop-blur-sm border border-[#1D214B]/10 rounded-2xl max-w-7xl mx-auto
        px-4 py-8 ${alignment[align]}`}
      >
        <div className="w-full sm:max-w-6xl">
          {/* Subtitle */}
          {subtitle && (
            <h4 className="text-sm sm:text-lg lg:text-2xl font-semibold tracking-tight text-[#1b1b1b]">
              {renderWithLineBreaks(subtitle)}
            </h4>
          )}

          {/* Title */}
          <h1
            className={`font-semibold tracking-tight ${highlightRed ? "text-[#cd]" : "text-[#272d57]"
              } text-lg sm:text-2xl lg:text-4xl`}
          >
            {renderWithLineBreaks(title)}
          </h1>

          {/* Description */}
          {description && (
            <p className="text-sm sm:text-base  text-[#1b1b1b] leading-snug my-5">
              {renderWithLineBreaks(description)}
            </p>
          )}

          {/* Button */}
          {buttonLabel &&
            (buttonOnClick ? (
              <Button
                responsiveSize={{
                  mobile: "md",
                  tablet: "md",
                  desktop: "lg",
                }}
                onClick={buttonOnClick}
              >
                {buttonLabel}
              </Button>
            ) : (
              <Link href={buttonLink || "/contact-us"}>
                <Button
                  responsiveSize={{
                    mobile: "md",
                    tablet: "md",
                    desktop: "lg",
                  }}
                >
                  {buttonLabel}
                </Button>
              </Link>
            ))}
        </div>
      </div>
    </section>
  );
};

export default PrimaryPromotionBanner;
